<template>
  <div class="container-fluid mt-3 mis-report-page">
    <!-- Header -->
    <div class="body-title mb-3">
      <b-row class="no-gutters align-items-center">
        <b-col>
          <h5 class="dash-heading"><i class="fas fa-chart-pie mr-2 text-primary"></i>MIS Reports</h5>
        </b-col>
      </b-row>
    </div>

    <!-- Filter Card -->
    <b-card class="mb-3 report-card shadow-sm" no-body>
      <div class="card-header bg-white border-bottom py-2 px-3 d-flex justify-content-between align-items-center flex-wrap">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-2 font-weight-bold text-secondary small">SELECT REPORT:</label>
          <b-form-select
            v-model="selectedReport"
            :options="options"
            size="sm"
            class="report-select font-weight-bold text-dark"
            @change="getReportData"
          ></b-form-select>
        </div>
        <div class="mt-2 mt-sm-0 d-flex align-items-center">
          <b-input-group size="sm" class="mr-2 table-search-box" v-if="tableData.length > 0">
            <b-input-group-prepend is-text class="bg-light border-right-0">
              <i class="fas fa-search text-muted small"></i>
            </b-input-group-prepend>
            <b-form-input
              v-model="tableFilter"
              placeholder="Search table..."
              class="border-left-0"
            ></b-form-input>
            <b-input-group-append v-if="tableFilter">
              <b-button variant="light" @click="tableFilter = ''" size="sm">×</b-button>
            </b-input-group-append>
          </b-input-group>

          <b-button
            variant="success"
            size="sm"
            class="export-btn shadow-sm"
            :disabled="!tableData.length"
            @click="exportToExcel"
          >
            <i class="fas fa-file-excel mr-1"></i> Export to Excel
          </b-button>
        </div>
      </div>

      <b-card-body class="p-3">
        <b-alert v-if="selectedReport == 7" show variant="info" class="py-2 px-3 mb-3 small">
          <i class="fas fa-info-circle mr-1"></i>
          <strong>Scheme-wise Progress Report:</strong> Displays application breakdown per scheme across all approval stages.
        </b-alert>

        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary" class="mb-2"></b-spinner>
          <div class="text-muted small">Loading report data from database...</div>
        </div>

        <!-- Table View -->
        <div v-else class="table-responsive">
          <b-table
            striped
            hover
            bordered
            small
            responsive
            head-variant="light"
            :items="tableData"
            :fields="fields"
            :filter="tableFilter"
            class="modern-mis-table mb-2"
            empty-text="No data available for the selected report"
            show-empty
          >
            <template #cell(IL_Applications)="row">
              <span class="font-weight-600 text-dark">{{ formatNumber(row.item.IL_Applications) }}</span>
            </template>
            <template #cell(GL_Applications)="row">
              <span class="font-weight-600 text-dark">{{ formatNumber(row.item.GL_Applications) }}</span>
            </template>
            <template #cell(EL_Applications)="row">
              <span class="font-weight-600 text-dark">{{ formatNumber(row.item.EL_Applications) }}</span>
            </template>
            <template #cell(TotalApplications)="row">
              <span class="badge badge-primary font-weight-bold px-2 py-1">{{ formatNumber(row.item.TotalApplications) }}</span>
            </template>
          </b-table>

          <!-- Summary Strip -->
          <div v-if="tableData.length > 0" class="d-flex justify-content-between align-items-center bg-light p-2 rounded border small">
            <span class="text-muted font-weight-bold">
              Total Categories / Rows: <strong>{{ tableData.length }}</strong>
            </span>
            <span class="text-primary font-weight-bold">
              Grand Total Applications: <strong class="text-dark">{{ formatNumber(grandTotalApplications) }}</strong>
            </span>
          </div>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import apis from "@/apis";
import XLSX from "xlsx";
import { normalizeResponseArray } from "@/utils/responseNormalizer";
import { TENANT_MAP } from "@/tenantConfig";

export default {
  name: "misReport",
  data() {
    return {
      selectedReport: 1,
      options: [
        { value: 1, text: "District wise Report" },
        { value: 2, text: "Division wise Report" },
        { value: 3, text: "Gender wise Report" },
        { value: 4, text: "Caste wise Report" },
        { value: 5, text: "Age wise Report" },
        { value: 6, text: "Loan Amount wise Report" },
        { value: 7, text: "Scheme-wise Progress/Status Report" },
        { value: 8, text: "Portal wise Report" },
      ],
      districtReportData: [],
      divisionReportData: [],
      genderReportData: [],
      casteReportData: [],
      ageReportData: [],
      amountReportData: [],
      schemeProgressData: [],
      schemeProgressStatuses: [],
      portalReportData: [],
      tableData: [],
      tableFilter: "",
      fields: [],
      isLoading: false,
      ws_name: "District",
    };
  },
  computed: {
    grandTotalApplications() {
      return (this.tableData || []).reduce((sum, row) => {
        return sum + (Number(row.TotalApplications) || 0);
      }, 0);
    },
  },
  methods: {
    formatNumber(num) {
      if (num === undefined || num === null || Number.isNaN(Number(num))) return "0";
      return Number(num).toLocaleString("en-IN");
    },
    exportToExcel() {
      if (!this.tableData || !this.tableData.length) return;

      const filename = this.ws_name + "_Wise_Report.xlsx";
      const wb = XLSX.utils.book_new();

      const exportHeaders = (this.fields || []).map((f) => (typeof f === "string" ? f : f.label || f.key));
      const exportKeys = (this.fields || []).map((f) => (typeof f === "string" ? f : f.key));

      const exportRows = this.tableData.map((row) => {
        const item = {};
        (this.fields || []).forEach((f) => {
          const key = typeof f === "string" ? f : f.key;
          const header = typeof f === "string" ? f : f.label || f.key;
          item[header] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        });
        return item;
      });

      const ws_data = [
        ["MAHARASHTRA STATE OBC FINANCE & DEVELOPMENT CORPORATION LTD"],
        [`MIS Report — ${this.ws_name} Wise Summary`],
        [],
      ];
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      XLSX.utils.sheet_add_json(ws, exportRows, {
        header: exportHeaders,
        skipHeader: false,
        origin: "A4",
      });

      // Add Grand Total row
      const dataEndRow = exportRows.length + 4;
      const totalsRow = exportKeys.map((k, idx) => {
        if (idx === 0) return "GRAND TOTAL";
        if (k === "IL_Applications") return this.tableData.reduce((s, r) => s + (Number(r.IL_Applications) || 0), 0);
        if (k === "GL_Applications") return this.tableData.reduce((s, r) => s + (Number(r.GL_Applications) || 0), 0);
        if (k === "EL_Applications") return this.tableData.reduce((s, r) => s + (Number(r.EL_Applications) || 0), 0);
        if (k === "TotalApplications") return this.grandTotalApplications;
        return "";
      });
      XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: `A${dataEndRow + 1}` });

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(exportHeaders.length - 1, 1) } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: Math.max(exportHeaders.length - 1, 1) } },
      ];

      XLSX.utils.book_append_sheet(wb, ws, this.ws_name.slice(0, 31));
      XLSX.writeFile(wb, filename);
    },

    getRegionByDistrictName(districtName) {
      const name = String(districtName || "").toLowerCase().trim();
      if (["mumbai city", "mumbai suburban", "thane", "palghar", "raigad", "ratnagiri", "sindhudurg", "मुंबई शहर", "मुंबई उपनगर", "ठाणे", "पालघर", "रायगड", "रत्नागिरी", "सिंधुदुर्ग"].indexOf(name) !== -1) return "Konkan";
      if (["pune", "satara", "sangli", "solapur", "kolhapur", "पुणे", "सातारा", "सांगली", "सोलापूर", "कोल्हापूर"].indexOf(name) !== -1) return "Pune";
      if (["nashik", "jalgaon", "ahilyanagar", "ahmednagar", "dhule", "nandurbar", "नाशिक", "जळगाव", "अहिल्यानगर", "अहमदनगर", "धुळे", "नंदुरबार"].indexOf(name) !== -1) return "Nashik";
      if (["chhatrapati sambhajinagar", "aurangabad", "jalna", "beed", "dharashiv", "osmanabad", "latur", "nanded", "hingoli", "parbhani", "छत्रपती संभाजीनगर", "औरंगाबाद", "जालना", "बीड", "धाराशिव", "उस्मानाबाद", "लातूर", "नांदेड", "हिंगोली", "परभणी"].indexOf(name) !== -1) return "Chhatrapati Sambhajinagar";
      if (["amravati", "akola", "buldhana", "washim", "yavatmal", "अमरावती", "अकोला", "बुलढाणा", "वाशीम", "यवतमाळ"].indexOf(name) !== -1) return "Amravati";
      if (["nagpur", "wardha", "bhandara", "gondia", "chandrapur", "gadchiroli", "नागपूर", "वर्धा", "भंडारा", "गोंदिया", "चंद्रपूर", "गडचिरोली"].indexOf(name) !== -1) return "Nagpur";
      return "Other";
    },

    buildSummariesFromApplications(applications) {
      const distMap = {};
      const divMap = {};
      const genderMap = {};
      const casteMap = {};
      const ageMap = {};
      const amountMap = {};

      const inc = (map, key, loanType) => {
        const k = String(key || "").trim() || "Not Known";
        if (!map[k]) {
          map[k] = { label: k, IL_Applications: 0, GL_Applications: 0, EL_Applications: 0, TotalApplications: 0 };
        }
        if (loanType === 1) map[k].IL_Applications++;
        else if (loanType === 2) map[k].GL_Applications++;
        else if (loanType === 3) map[k].EL_Applications++;
        map[k].TotalApplications++;
      };

      (applications || []).forEach((row) => {
        const loanType = Number(row.LoanType || row.LoanTypeID || 1);
        const dist = row.DistrictName || row.District || row.DistName || "Not Known";
        const div = row.Division || row.OfficeName || this.getRegionByDistrictName(dist);
        const gender = row.Gender === 1 || row.Gender === "1" || String(row.Gender).toLowerCase() === "male"
          ? "Male"
          : (row.Gender === 0 || row.Gender === "0" || String(row.Gender).toLowerCase() === "female" ? "Female" : (row.Gender || "Other"));
        const caste = row.CasteMarathi || row.CasteName || row.Caste || "Not Known";
        const age = row.Age || row.ApplicantAge || "Not Known";
        const amt = row.AmountDemanded || row.BankLoanAmount || "Not Known";

        inc(distMap, dist, loanType);
        inc(divMap, div, loanType);
        inc(genderMap, gender, loanType);
        inc(casteMap, caste, loanType);
        inc(ageMap, age, loanType);
        inc(amountMap, amt, loanType);
      });

      this.districtReportData = Object.values(distMap).map((x) => ({ District: x.label, ...x })).sort((a, b) => a.District.localeCompare(b.District));
      this.divisionReportData = Object.values(divMap).map((x) => ({ Division: x.label, ...x })).sort((a, b) => a.Division.localeCompare(b.Division));
      this.genderReportData = Object.values(genderMap).map((x) => ({ Gender: x.label, ...x })).sort((a, b) => a.Gender.localeCompare(b.Gender));
      this.casteReportData = Object.values(casteMap).map((x) => ({ Caste: x.label, ...x })).sort((a, b) => a.Caste.localeCompare(b.Caste));
      this.ageReportData = Object.values(ageMap).map((x) => ({ Age: x.label, ...x })).sort((a, b) => String(a.Age).localeCompare(String(b.Age), undefined, { numeric: true }));
      this.amountReportData = Object.values(amountMap).map((x) => ({ LoanAmount: x.label, ...x })).sort((a, b) => String(a.LoanAmount).localeCompare(String(b.LoanAmount), undefined, { numeric: true }));
    },

    loadSummaryReports() {
      const districtId = this.$store.getters.getUserDistrict || "00000000-0000-0000-0000-000000000000";
      return apis.ReadSummaryReports(districtId)
        .then((response) => {
          if (response && response.status === 200 && Array.isArray(response.body) && response.body.length >= 6) {
            const mapRow = (x, keyField) => ({
              [keyField]: x[keyField] || x.DistrictName || x.Division || x.Caste || x.Gender || x.Age || x.LoanAmount || x.AmountDemanded || "Unknown",
              IL_Applications: Number(x.ILApplications || x.IL_Applications || x.TotalIndividual || 0),
              GL_Applications: Number(x.GLApplications || x.GL_Applications || x.TotalGroup || 0),
              EL_Applications: Number(x.ELApplications || x.EL_Applications || x.TotalEducation || 0),
              TotalApplications: Number(x.TotalApplications || 0),
            });

            // If response.body has 7 tables (indices 1 to 6) or 6 lists (indices 0 to 5)
            const isRawTableStructure = response.body.length >= 7 && response.body[1] && response.body[1][0] && response.body[1][0].DistrictName;
            const distIndex = isRawTableStructure ? 1 : 0;
            const divIndex = isRawTableStructure ? 2 : 1;
            const casteIndex = isRawTableStructure ? 3 : 2;
            const genderIndex = isRawTableStructure ? 4 : 3;
            const ageIndex = isRawTableStructure ? 5 : 4;
            const amtIndex = isRawTableStructure ? 6 : 5;

            this.districtReportData = (response.body[distIndex] || []).map((x) => mapRow(x, "District"));
            this.divisionReportData = (response.body[divIndex] || []).map((x) => mapRow(x, "Division"));
            this.casteReportData = (response.body[casteIndex] || []).map((x) => mapRow(x, "Caste"));
            this.genderReportData = (response.body[genderIndex] || []).map((x) => mapRow(x, "Gender"));
            this.ageReportData = (response.body[ageIndex] || []).map((x) => mapRow(x, "Age"));
            this.amountReportData = (response.body[amtIndex] || []).map((x) => mapRow(x, "LoanAmount"));
          } else {
            throw new Error("Invalid response format, falling back to applications list");
          }
        })
        .catch(() => {
          // Robust Fallback: Fetch application list across all loan types and calculate summaries client-side
          const loanTypes = [1, 2, 3];
          const requests = loanTypes.map((loanType) =>
            apis.ReadApplicationList({
              DistrictID: districtId,
              ApplicationStatusID: 0,
              LoanType: loanType,
              MobileNo: "",
              ApplicationNo: "",
              StartDate: "01-01-1970",
              EndDate: "01-01-1970",
            })
              .then((r) => normalizeResponseArray(r))
              .catch(() => [])
          );

          return Promise.all(requests).then((groups) => {
            const applications = [].concat.apply([], groups || []);
            this.buildSummariesFromApplications(applications);
          });
        });
    },

    buildSchemeProgressReport(applications, statusList) {
      const statusLabels = [];
      const seen = new Set();

      (statusList || []).forEach((item) => {
        const label = String(item.ApplicationStatus || item.StatusName || item.Name || item.Status || "").trim();
        if (label && !seen.has(label)) {
          seen.add(label);
          statusLabels.push(label);
        }
      });

      const schemeMap = {};
      (applications || []).forEach((row) => {
        const scheme = String(row.SchemeType || row.SchemeName || row.LoanTypeName || row.Scheme || "Other").trim();
        const status = String(row.ApplicationStatus || row.StatusName || row.Status || "Unknown").trim();

        if (!schemeMap[scheme]) {
          schemeMap[scheme] = { Scheme: scheme, TotalApplications: 0 };
          statusLabels.forEach((label) => {
            schemeMap[scheme][label] = 0;
          });
        }

        if (statusLabels.indexOf(status) === -1) {
          statusLabels.push(status);
          Object.keys(schemeMap).forEach((schemeKey) => {
            schemeMap[schemeKey][status] = schemeMap[schemeKey][status] || 0;
          });
        }

        schemeMap[scheme][status] = (schemeMap[scheme][status] || 0) + 1;
        schemeMap[scheme].TotalApplications += 1;
      });

      this.schemeProgressStatuses = statusLabels;
      this.schemeProgressData = Object.keys(schemeMap)
        .map((scheme) => schemeMap[scheme])
        .sort((a, b) => a.Scheme.localeCompare(b.Scheme));
    },

    loadSchemeProgressReport() {
      const districtId = this.$store.getters.getUserDistrict || "00000000-0000-0000-0000-000000000000";
      const loanTypes = [1, 2, 3];

      return Promise.all([
        apis.getApplicationStatusMstr().then((r) => normalizeResponseArray(r)).catch(() => []),
        Promise.all(
          loanTypes.map((loanType) =>
            apis.ReadApplicationList({
              DistrictID: districtId,
              ApplicationStatusID: 0,
              LoanType: loanType,
              MobileNo: "",
              ApplicationNo: "",
              StartDate: "01-01-1970",
              EndDate: "01-01-1970",
            })
              .then((r) => normalizeResponseArray(r))
              .catch(() => [])
          )
        ),
      ]).then(([statusList, appGroups]) => {
        const applications = [].concat.apply([], appGroups || []);
        this.buildSchemeProgressReport(applications, statusList);
      }).catch(() => {});
    },

    loadPortalWiseReport() {
      const districtId = this.$store.getters.getUserDistrict || "00000000-0000-0000-0000-000000000000";
      const portals = Object.keys(TENANT_MAP).map((k) => TENANT_MAP[k]);

      return Promise.all(
        portals.map((portal) => {
          const casteCodesCsv = (portal.castes || [])
            .map((c) => c.casteCode)
            .filter((c) => Number(c) > 0)
            .join(",");
          const casteNamesCsv = (portal.castes || [])
            .map((c) => c.casteName)
            .filter((c) => c && String(c).trim() !== "")
            .join(",");

          const requests = [1, 2, 3].map((loanType) =>
            apis.ReadCompanyStatisticsSplit({
              DistrictID: districtId,
              LoanType: loanType,
              StartDate: "01-01-1970",
              EndDate: "31-12-2099",
              CasteCodesCsv: casteCodesCsv || null,
              CasteNamesCsv: casteNamesCsv || null,
            })
              .then((response) => {
                let body = response && (response.body || response.data);
                if (typeof body === "string") {
                  try {
                    body = JSON.parse(body);
                  } catch (e) {
                    body = null;
                  }
                }
                const tables = body ? (Array.isArray(body) ? body : Object.values(body)) : [];
                const schemeTable = tables[0] || [];
                return (schemeTable || []).reduce((sum, r) => sum + (Number(r.TotalApplications || r.Count || r[1]) || 0), 0);
              })
              .catch(() => 0)
          );

          return Promise.all(requests).then((counts) => ({
            PortalType: portal.isParent ? "Main Company" : "Subsidiary",
            PortalName: portal.nameMarathi || portal.nameEnglish || portal.tenantId,
            IL_Applications: counts[0] || 0,
            GL_Applications: counts[1] || 0,
            EL_Applications: counts[2] || 0,
            TotalApplications: (counts[0] || 0) + (counts[1] || 0) + (counts[2] || 0),
          }));
        })
      ).then((rows) => {
        this.portalReportData = rows;
      }).catch(() => {});
    },

    getReportData(val) {
      const id = String(val || this.selectedReport || "1");
      const baseFields = [
        { key: "IL_Applications", label: "IL Applications", sortable: true },
        { key: "GL_Applications", label: "GL Applications", sortable: true },
        { key: "EL_Applications", label: "EL Applications", sortable: true },
        { key: "TotalApplications", label: "Total Applications", sortable: true },
      ];

      switch (id) {
        case "1":
          this.tableData = this.districtReportData;
          this.fields = [{ key: "District", label: "District", sortable: true }].concat(baseFields);
          this.ws_name = "District";
          break;
        case "2":
          this.tableData = this.divisionReportData;
          this.fields = [{ key: "Division", label: "Division", sortable: true }].concat(baseFields);
          this.ws_name = "Division";
          break;
        case "3":
          this.tableData = this.genderReportData;
          this.fields = [{ key: "Gender", label: "Gender", sortable: true }].concat(baseFields);
          this.ws_name = "Gender";
          break;
        case "4":
          this.tableData = this.casteReportData;
          this.fields = [{ key: "Caste", label: "Caste", sortable: true }].concat(baseFields);
          this.ws_name = "Caste";
          break;
        case "5":
          this.tableData = this.ageReportData;
          this.fields = [{ key: "Age", label: "Age Group", sortable: true }].concat(baseFields);
          this.ws_name = "Age";
          break;
        case "6":
          this.tableData = this.amountReportData;
          this.fields = [{ key: "LoanAmount", label: "Amount Range", sortable: true }].concat(baseFields);
          this.ws_name = "Loan Amount";
          break;
        case "7":
          this.tableData = this.schemeProgressData;
          this.fields = [{ key: "Scheme", label: "Scheme Name", sortable: true }]
            .concat((this.schemeProgressStatuses || []).map((status) => ({ key: status, label: status, sortable: true })))
            .concat([{ key: "TotalApplications", label: "Total Applications", sortable: true }]);
          this.ws_name = "Scheme Progress";
          break;
        case "8":
          this.tableData = this.portalReportData;
          this.fields = [
            { key: "PortalType", label: "Category", sortable: true },
            { key: "PortalName", label: "Portal / Company Name", sortable: true },
          ].concat(baseFields);
          this.ws_name = "Portal";
          break;
        default:
          this.tableData = this.districtReportData;
          this.fields = [{ key: "District", label: "District", sortable: true }].concat(baseFields);
          this.ws_name = "District";
      }
    },
  },
  mounted() {
    this.isLoading = true;
    Promise.all([
      this.loadSummaryReports(),
      this.loadSchemeProgressReport(),
      this.loadPortalWiseReport(),
    ])
      .finally(() => {
        this.isLoading = false;
        this.getReportData(this.selectedReport || 1);
      });
  },
};
</script>

<style scoped>
.mis-report-page {
  padding-bottom: 30px;
}
.dash-heading {
  font-size: 20px;
  font-weight: 700;
  color: #133a6d;
  padding: 6px 0 2px;
}
.report-card {
  border-radius: 10px;
  border: 1px solid #dce6f5;
}
.report-select {
  width: auto;
  min-width: 250px;
  border-radius: 6px;
  border-color: #cbd5e1;
}
.table-search-box {
  max-width: 200px;
}
.export-btn {
  font-weight: 600;
  border-radius: 6px;
  padding: 5px 14px;
}
.modern-mis-table {
  font-size: 13px;
}
.modern-mis-table th {
  background-color: #f1f5f9 !important;
  color: #334155;
  font-weight: 700;
  border-bottom: 2px solid #cbd5e1;
}
.font-weight-600 {
  font-weight: 600;
}
</style>




// WEBPACK FOOTER //
// src/components/Admin/misReport.vue